import React, { useState, useEffect, useRef } from 'react';
import cityCodesData from '../constant/city_codes.json';
import { MdOutlineCancel } from 'react-icons/md';
import RegionDropdownMetrix from './RegionDropdownMetrix';
import { useAptMetrixContext } from '../context/AptMetrixContext';

const FilterComponent = ({ 
    data,
    onRegionSelect,
    onPriceFilterChange,
    onHouseholdFilterChange,
    onAreaFilterChange,
    onGapFilterChange,
    onMoveInDateFilterChange,  // 새로운 prop 추가
    onSubdistrictFilterChange,  // 새로운 prop 추가
    onSubwayFilterChange,  // 새로운 prop 추가
    onGangnamTimeFilterChange,  // 강남역 소요시간 필터 prop 추가
    onSchoolFilterChange,  // 초등학교 필터 prop 추가
}) => {
    // Context에서 상태 가져오기
    const { 
        filterUIState, 
        setFilterUIState,
        filterComponentState,
        setFilterComponentState,
        setPriceFilter,
        setHouseholdFilter,
        setAreaFilter,
        setGapFilter,
        setMoveInDateFilter,
        setSubdistrictFilter,
        setSubwayFilter,
        setGangnamTimeFilter,
        setSchoolFilter
    } = useAptMetrixContext();
    
    // Context의 filterComponentState에서 값 가져오기
    const { selectedRegion, subdistrictOptions, subwayLines } = filterComponentState;
    const selectedCities = filterComponentState.selectedCities;
    
    // Context 상태 업데이트 함수들
    const setSelectedRegion = (value) => setFilterComponentState(prev => ({ ...prev, selectedRegion: value }));
    const setSelectedCities = (value) => setFilterComponentState(prev => ({ ...prev, selectedCities: value }));
    const setSubdistrictOptions = (value) => setFilterComponentState(prev => ({ ...prev, subdistrictOptions: value }));
    const setSubwayLines = (value) => setFilterComponentState(prev => ({ ...prev, subwayLines: value }));
    
    const [siDoData, setSiDoData] = useState(null);
    const [siGunGuData, setSiGunGuData] = useState(null);
    
    // 툴팁 표시 상태 (UI만)
    const [showPriceTooltip, setShowPriceTooltip] = useState(false);
    const [showHouseholdTooltip, setShowHouseholdTooltip] = useState(false);
    const [showAreaTooltip, setShowAreaTooltip] = useState(false);
    const [showGapTooltip, setShowGapTooltip] = useState(false);
    const [showMoveInDateTooltip, setShowMoveInDateTooltip] = useState(false);
    const [showSubdistrictTooltip, setShowSubdistrictTooltip] = useState(false);
    const [showSubwayTooltip, setShowSubwayTooltip] = useState(false);
    const [showWalkingTimeTooltip, setShowWalkingTimeTooltip] = useState(false);
    const [showGangnamTimeTooltip, setShowGangnamTimeTooltip] = useState(false);
    const [showSchoolTooltip, setShowSchoolTooltip] = useState(false);
    
    // Context의 filterUIState 값들을 개별 변수로 매핑
    const { 
        minPrice, maxPrice, isPriceFilterActive,
        minHousehold, maxHousehold, isHouseholdFilterActive,
        minArea, maxArea, isAreaFilterActive,
        minGap, maxGap, isGapFilterActive,
        minYears, maxYears, isMoveInDateFilterActive,
        selectedSubdistricts, isSubdistrictFilterActive,
        selectedSubwayLines, isSubwayFilterActive,
        maxWalkingTime, isWalkingTimeFilterActive,
        maxGangnamTime, isGangnamTimeFilterActive,
        hasSchool, isSchoolFilterActive
    } = filterUIState;
    
    // 상태 업데이트 헬퍼 함수
    const updateFilterUIState = (updates) => {
        setFilterUIState(prev => ({ ...prev, ...updates }));
    };
    
    // setter 함수들을 updateFilterUIState를 사용하도록 래핑
    const setMinPrice = (value) => updateFilterUIState({ minPrice: value });
    const setMaxPrice = (value) => updateFilterUIState({ maxPrice: value });
    const setIsPriceFilterActive = (value) => updateFilterUIState({ isPriceFilterActive: value });
    
    const setMinHousehold = (value) => updateFilterUIState({ minHousehold: value });
    const setMaxHousehold = (value) => updateFilterUIState({ maxHousehold: value });
    const setIsHouseholdFilterActive = (value) => updateFilterUIState({ isHouseholdFilterActive: value });
    
    const setMinArea = (value) => updateFilterUIState({ minArea: value });
    const setMaxArea = (value) => updateFilterUIState({ maxArea: value });
    const setIsAreaFilterActive = (value) => updateFilterUIState({ isAreaFilterActive: value });
    
    const setMinGap = (value) => updateFilterUIState({ minGap: value });
    const setMaxGap = (value) => updateFilterUIState({ maxGap: value });
    const setIsGapFilterActive = (value) => updateFilterUIState({ isGapFilterActive: value });
    
    const setMinYears = (value) => updateFilterUIState({ minYears: value });
    const setMaxYears = (value) => updateFilterUIState({ maxYears: value });
    const setIsMoveInDateFilterActive = (value) => updateFilterUIState({ isMoveInDateFilterActive: value });
    
    const setSelectedSubdistricts = (value) => updateFilterUIState({ selectedSubdistricts: value });
    const setIsSubdistrictFilterActive = (value) => updateFilterUIState({ isSubdistrictFilterActive: value });
    
    const setSelectedSubwayLines = (value) => updateFilterUIState({ selectedSubwayLines: value });
    const setIsSubwayFilterActive = (value) => updateFilterUIState({ isSubwayFilterActive: value });
    
    const setMaxWalkingTime = (value) => updateFilterUIState({ maxWalkingTime: value });
    const setIsWalkingTimeFilterActive = (value) => updateFilterUIState({ isWalkingTimeFilterActive: value });
    
    const setMaxGangnamTime = (value) => updateFilterUIState({ maxGangnamTime: value });
    const setIsGangnamTimeFilterActive = (value) => updateFilterUIState({ isGangnamTimeFilterActive: value });
    
    const setHasSchool = (value) => updateFilterUIState({ hasSchool: value });
    const setIsSchoolFilterActive = (value) => updateFilterUIState({ isSchoolFilterActive: value });

    // 툴팁 참조 생성
    const priceTooltipRef = useRef(null);
    const priceButtonRef = useRef(null);
    const householdTooltipRef = useRef(null);
    const householdButtonRef = useRef(null);
    const areaTooltipRef = useRef(null);
    const areaButtonRef = useRef(null);
    const gapTooltipRef = useRef(null);
    const gapButtonRef = useRef(null);
    const moveInDateTooltipRef = useRef(null);
    const moveInDateButtonRef = useRef(null);
    const subdistrictTooltipRef = useRef(null);
    const subdistrictButtonRef = useRef(null);
    const subwayTooltipRef = useRef(null);
    const subwayButtonRef = useRef(null);
    const walkingTimeTooltipRef = useRef(null);
    const walkingTimeButtonRef = useRef(null);
    const gangnamTimeTooltipRef = useRef(null);
    const gangnamTimeButtonRef = useRef(null);
    const schoolTooltipRef = useRef(null);
    const schoolButtonRef = useRef(null);

    // JSON 데이터를 사용하여 시도 데이터 설정
    useEffect(() => {
        const uniqueRegions = [
            ...[...new Map(cityCodesData.map(item => [item['지역명'], item['지역코드']])).values()]
                .map(code => ({ cortarName: cityCodesData.find(item => item['지역코드'] === code)['지역명'], cortarNo: code }))
        ];
        setSiDoData(uniqueRegions);
    }, []);

    useEffect(() => {
        if (selectedRegion) {
            const cities = [
                ...cityCodesData
                    .filter(item => item['지역코드'] === selectedRegion)
                    .map(item => ({ cortarName: item['시군구명'], cortarNo: item['시군구코드'] }))
            ];
            setSiGunGuData([...new Map(cities.map(city => [city.cortarNo, city])).values()]);
        }
    }, [selectedRegion]);

    // 이전 데이터를 추적하기 위한 ref
    const prevDataRef = useRef(null);
    const prevDataHashRef = useRef(null);
    
    // 데이터에서 고유한 동(subdistrict_name) 목록 추출 - 수정
    useEffect(() => {
        if (data && Array.isArray(data) && data.length > 0) {
            // 데이터의 실제 내용이 변경되었는지 확인하기 위한 해시 생성
            const currentDataHash = JSON.stringify({
                length: data.length,
                firstComplexNo: data[0]?.complex_no,
                lastComplexNo: data[data.length - 1]?.complex_no,
                // 데이터의 중요한 식별자들을 포함
                complexNos: data.slice(0, Math.min(5, data.length)).map(item => item.complex_no).join(',')
            });
            
            // 데이터가 실제로 변경되었는지 확인
            const isNewData = prevDataHashRef.current !== currentDataHash;
            
            const uniqueSubdistricts = [...new Set(data.map(item => item.subdistrict_name))]
                .filter(name => name) // null, undefined, empty string 제거
                .sort(); // 알파벳 순으로 정렬
            
            setSubdistrictOptions(uniqueSubdistricts);
            
            // 새로운 데이터일 때만 필터 초기화
            // 필터가 활성화되어 있지 않고, 선택된 항목이 없을 때만
            if (isNewData && !isSubdistrictFilterActive && selectedSubdistricts.length === 0) {
                setSelectedSubdistricts(uniqueSubdistricts);
            }

            // 지하철역 정보 추출
            collectSubwayStations(data);
            
            // 현재 데이터와 해시를 저장
            prevDataRef.current = data;
            prevDataHashRef.current = currentDataHash;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    // 지하철역 정보 수집 함수
    const collectSubwayStations = (data) => {
        // 지하철 노선 정보를 저장할 맵
        const lineMap = new Map();
        
        data.forEach(item => {
            // 지하철 데이터가 있는 경우만 처리
            if (item.subwaystationname && item.subwaystationname.trim() !== '' && 
                item.subwaylinename && item.subwaylinename.trim() !== '') {
                // 역 이름과 노선 정보 추출
                const stationNames = item.subwaystationname.split(',');
                const lineNames = item.subwaylinename.split(',');
                const lineColors = item.subwaylinecolor ? item.subwaylinecolor.split(',') : [];
                
                // 각 역에 대한 처리
                stationNames.forEach((stationName, index) => {
                    const station = stationName.trim();
                    
                    // 노선 정보 추출 (파이프로 구분된 여러 노선이 있을 수 있음)
                    const lines = lineNames[index] ? lineNames[index].split('|') : [];
                    const colors = lineColors[index] ? lineColors[index].split('|') : [];
                    
                    // 각 노선에 대한 처리
                    lines.forEach((line, i) => {
                        const lineName = line.trim();
                        const lineColor = colors[i] ? colors[i].trim() : '#888';
                        
                        // 노선 정보 맵에 추가
                        if (lineMap.has(lineName)) {
                            // 기존 노선 정보 가져오기
                            const lineInfo = lineMap.get(lineName);
                            // 이미 있는 역이 아닌 경우에만 추가
                            if (!lineInfo.stations.includes(station)) {
                                lineInfo.stations.push(station);
                            }
                        } else {
                            // 새로운 노선 정보 추가
                            lineMap.set(lineName, {
                                name: lineName,
                                color: lineColor,
                                stations: [station]
                            });
                        }
                    });
                });
            }
        });
        
        // 노선 정보를 배열로 변환하고 노선 이름 기준으로 정렬
        const lineList = Array.from(lineMap.values()).sort((a, b) => {
            // 숫자로 된 노선은 숫자 크기 순으로 정렬 (1호선, 2호선, ...)
            const numA = parseInt(a.name.replace(/[^0-9]/g, ''));
            const numB = parseInt(b.name.replace(/[^0-9]/g, ''));
            
            if (!isNaN(numA) && !isNaN(numB)) {
                return numA - numB;
            }
            
            // 숫자가 아니면 문자열 정렬
            return a.name.localeCompare(b.name, 'ko-KR');
        });
        
        setSubwayLines(lineList);
    };

    // 외부 클릭 시 툴팁 닫기
    useEffect(() => {
        function handleClickOutside(event) {
            if (priceTooltipRef.current && 
                !priceTooltipRef.current.contains(event.target) &&
                !priceButtonRef.current.contains(event.target)) {
                setShowPriceTooltip(false);
            }
            
            if (householdTooltipRef.current && 
                !householdTooltipRef.current.contains(event.target) &&
                !householdButtonRef.current.contains(event.target)) {
                setShowHouseholdTooltip(false);
            }
            
            if (areaTooltipRef.current && 
                !areaTooltipRef.current.contains(event.target) &&
                !areaButtonRef.current.contains(event.target)) {
                setShowAreaTooltip(false);
            }

            // 전매갭 필터 툴팁 처리 추가
            if (gapTooltipRef.current && 
                !gapTooltipRef.current.contains(event.target) &&
                !gapButtonRef.current.contains(event.target)) {
                setShowGapTooltip(false);
            }

            // 입주일 필터 툴팁 처리 추가
            if (moveInDateTooltipRef.current && 
                !moveInDateTooltipRef.current.contains(event.target) &&
                !moveInDateButtonRef.current.contains(event.target)) {
                setShowMoveInDateTooltip(false);
            }

            // 동 필터 툴팁 처리 추가
            if (subdistrictTooltipRef.current && 
                !subdistrictTooltipRef.current.contains(event.target) &&
                !subdistrictButtonRef.current.contains(event.target)) {
                setShowSubdistrictTooltip(false);
            }

            // 지하철 필터 툴팁 처리 추가
            if (subwayTooltipRef.current && 
                !subwayTooltipRef.current.contains(event.target) &&
                !subwayButtonRef.current.contains(event.target)) {
                setShowSubwayTooltip(false);
            }
            
            // 도보 시간 필터 툴팁 처리 추가
            if (walkingTimeTooltipRef.current && 
                !walkingTimeTooltipRef.current.contains(event.target) &&
                !walkingTimeButtonRef.current.contains(event.target)) {
                setShowWalkingTimeTooltip(false);
            }
            
            // 강남역 소요시간 필터 툴팁 처리 추가
            if (gangnamTimeTooltipRef.current && 
                !gangnamTimeTooltipRef.current.contains(event.target) &&
                !gangnamTimeButtonRef.current.contains(event.target)) {
                setShowGangnamTimeTooltip(false);
            }
            
            // 초등학교 필터 툴팁 처리 추가
            if (schoolTooltipRef.current && 
                !schoolTooltipRef.current.contains(event.target) &&
                !schoolButtonRef.current.contains(event.target)) {
                setShowSchoolTooltip(false);
            }
        }
        
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // 가격 필터 버튼 상태 디버깅
    useEffect(() => {
        console.log("Button state:", {
            dataExists: !!data,
            dataLength: data ? data.length : 0,
        });
    }, [data]);

    const handleRegionChange = (e) => {
        const regionValue = e.target.value;
        setSelectedRegion(regionValue);
        setSelectedCities([]);
        
        // 상위 컴포넌트로 선택된 지역 정보 전달
        const regionName = siDoData?.find(r => r.cortarNo === regionValue)?.cortarName || '';
        const regionData = { 
            regionCode: regionValue,
            cityCode: "",
            regionName: regionName,
            cityName: ""
        };
        
        onRegionSelect(regionData);
        
        // 사용자가 실제로 지역을 변경했을 때만 필터 초기화
        // (autoSearch가 false이면 컴포넌트 리렌더링으로 인한 호출이므로 필터를 리셋하지 않음)
        if (e.userInitiated !== false) {
            resetAllFilters();
        }
        
        // 자동 검색 플래그가 있고 지역이 선택된 경우 자동 검색 실행
        if (e.autoSearch && regionValue) {
            triggerSearch(regionData);
        }
    };
    
    const handleCityChange = (e) => {
        // 단일 시/군/구 선택 처리 - 하위 호환성 유지
        if (typeof e.target.value === 'string' && !e.selectedCities) {
            const cityValue = e.target.value;
            // 기존 방식 그대로 처리 (단일 시/군/구 선택)
            const regionName = siDoData?.find(r => r.cortarNo === selectedRegion)?.cortarName || '';
            const cityName = cityValue === "전체" ? "전체" : siGunGuData?.find(c => c.cortarNo === cityValue)?.cortarName || '';
            
            setSelectedCities(cityValue === "전체" ? [] : [{ cortarNo: cityValue, cortarName: cityName }]);
            
            const regionData = {
                regionCode: selectedRegion,
                cityCode: cityValue === "전체" ? "" : cityValue,
                regionName,
                cityName,
                multipleSelect: false
            };
            
            onRegionSelect(regionData);
            
            // 자동 검색 플래그가 있는 경우 검색 실행
            if (e.autoSearch) {
                triggerSearch(regionData);
            }
            
            return;
        }
        
        // 다중 시/군/구 선택 처리
        if (e.selectedCities) {
            setSelectedCities(e.selectedCities);
            
            const regionName = e.regionName || siDoData?.find(r => r.cortarNo === selectedRegion)?.cortarName || '';
            
            // 여러 시/군/구 코드를 배열로 저장
            const cityCodes = e.selectedCities.map(city => city.cortarNo);
            // 여러 시/군/구 이름을 배열로 저장
            const cityNames = e.selectedCities.map(city => city.cortarName);
            
            const regionData = {
                regionCode: selectedRegion,
                cityCode: cityCodes,
                regionName,
                cityName: cityNames,
                multipleSelect: true
            };
            
            onRegionSelect(regionData);
            
            // 자동 검색 플래그가 있는 경우 검색 실행
            if (e.autoSearch) {
                triggerSearch(regionData);
            }
        }
    };

    // 검색 트리거 함수
    const triggerSearch = (regionData) => {
        // 다중 선택 여부 확인
        const isMultipleSelect = regionData.multipleSelect || false;
        const isMultipleRegions = regionData.multipleRegions || false;
        
        console.log("트리거 검색 함수 호출됨:", regionData);
        
        // regionData.cityCode가 배열인 경우 (다중 선택)
        if ((isMultipleSelect || isMultipleRegions) && Array.isArray(regionData.cityCode)) {
            const updatedRegionData = {
                ...regionData,
                searchClicked: true
            };
            
            console.log("다중 선택 검색 트리거:", updatedRegionData);
            onRegionSelect(updatedRegionData);
        } 
        // 단일 선택 또는 전체 선택인 경우 (기존 방식)
        else {
            const updatedRegionData = {
                ...regionData,
                searchClicked: true,
                // 시/군/구가 선택되지 않았을 때 cityName을 "전체"로 설정
                cityName: regionData.cityCode === "" && regionData.regionCode !== "" ? "전체" : regionData.cityName
            };
            
            console.log("단일 선택 검색 트리거:", updatedRegionData);
            onRegionSelect(updatedRegionData);
        }
    };

    // 가격 필터 적용 함수
    const applyPriceFilter = () => {
        // 입력값을 숫자로 변환 (콤마 제거)
        const minPriceValue = minPrice ? parseInt(minPrice.replace(/,/g, ''), 10) : null;
        const maxPriceValue = maxPrice ? parseInt(maxPrice.replace(/,/g, ''), 10) : null;
        
        setIsPriceFilterActive(minPriceValue !== null || maxPriceValue !== null);
        
        // Context의 실제 필터 상태 업데이트
        setPriceFilter({
            minPrice: minPriceValue,
            maxPrice: maxPriceValue
        });
        
        // 상위 컴포넌트로 가격 필터 정보 전달
        onPriceFilterChange({
            minPrice: minPriceValue,
            maxPrice: maxPriceValue
        });
        
        setShowPriceTooltip(false);
    };

    // 가격 필터 초기화 함수
    const resetPriceFilter = () => {
        setMinPrice('');
        setMaxPrice('');
        setIsPriceFilterActive(false);
        
        // Context의 실제 필터 상태 초기화
        setPriceFilter({
            minPrice: null,
            maxPrice: null
        });
        
        // 상위 컴포넌트로 필터 초기화 전달
        onPriceFilterChange({
            minPrice: null,
            maxPrice: null
        });
        setShowPriceTooltip(false);
    };
    
    // 총세대수 필터 적용 함수
    const applyHouseholdFilter = () => {
        // 입력값을 숫자로 변환
        const minHouseholdValue = minHousehold ? parseInt(minHousehold, 10) : null;
        const maxHouseholdValue = maxHousehold ? parseInt(maxHousehold, 10) : null;
        
        setIsHouseholdFilterActive(minHouseholdValue !== null || maxHouseholdValue !== null);
        
        // Context의 실제 필터 상태 업데이트
        setHouseholdFilter({
            minHousehold: minHouseholdValue,
            maxHousehold: maxHouseholdValue
        });
        
        // 상위 컴포넌트로 총세대수 필터 정보 전달
        onHouseholdFilterChange({
            minHousehold: minHouseholdValue,
            maxHousehold: maxHouseholdValue
        });
        
        setShowHouseholdTooltip(false);
    };

    // 총세대수 필터 초기화 함수
    const resetHouseholdFilter = () => {
        setMinHousehold('');
        setMaxHousehold('');
        setIsHouseholdFilterActive(false);
        
        // Context의 실제 필터 상태 초기화
        setHouseholdFilter({
            minHousehold: null,
            maxHousehold: null
        });
        
        // 상위 컴포넌트로 필터 초기화 전달
        onHouseholdFilterChange({
            minHousehold: null,
            maxHousehold: null
        });
        setShowHouseholdTooltip(false);
    };
    
    // 전용면적 필터 적용 함수
    const applyAreaFilter = () => {
        // 입력값을 숫자로 변환
        const minAreaValue = minArea ? parseFloat(minArea) : null;
        const maxAreaValue = maxArea ? parseFloat(maxArea) : null;
        
        setIsAreaFilterActive(minAreaValue !== null || maxAreaValue !== null);
        
        // 상위 컴포넌트로 전용면적 필터 정보 전달
        onAreaFilterChange({
            minArea: minAreaValue,
            maxArea: maxAreaValue
        });
        
        setShowAreaTooltip(false);
    };

    // 전용면적 필터 초기화 함수
    const resetAreaFilter = () => {
        setMinArea('');
        setMaxArea('');
        setIsAreaFilterActive(false);
        
        // 상위 컴포넌트로 필터 초기화 전달
        onAreaFilterChange({
            minArea: null,
            maxArea: null
        });
        setShowAreaTooltip(false);
    };

    // 전매갭 필터 입력 처리 함수 수정 (숫자, 소수점만 허용에서 천단위 콤마 추가)
    const handleGapInput = (e, setter) => {
        const value = e.target.value;
        // 숫자와 콤마만 허용
        if (/^[0-9,]*$/.test(value)) {
            // 콤마 제거 후 숫자만 추출
            const numericValue = value.replace(/,/g, '');
            // 콤마 추가하여 표시
            if (numericValue) {
                setter(numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ','));
            } else {
                setter('');
            }
        }
    };

    // 전매갭 필터 적용 함수 수정
    const applyGapFilter = () => {
        // 입력값을 숫자로 변환 (콤마 제거)
        const minGapValue = minGap ? parseInt(minGap.replace(/,/g, ''), 10) : null;
        const maxGapValue = maxGap ? parseInt(maxGap.replace(/,/g, ''), 10) : null;
        
        console.log("전매갭 필터 적용:", { minGapValue, maxGapValue });
        
        setIsGapFilterActive(minGapValue !== null || maxGapValue !== null);
        
        // 상위 컴포넌트로 전매갭 필터 정보 전달
        onGapFilterChange({
            minGap: minGapValue,
            maxGap: maxGapValue
        });
        
        setShowGapTooltip(false);
    };
    
    // 전매갭 필터 초기화 함수
    const resetGapFilter = () => {
        setMinGap('');
        setMaxGap('');
        setIsGapFilterActive(false);
        
        // Context의 실제 필터 상태 초기화
        setGapFilter({
            minGap: null,
            maxGap: null
        });
        
        onGapFilterChange({
            minGap: null,
            maxGap: null
        });
        
        setShowGapTooltip(false);
    };

    // 입주일 필터 적용 함수 수정
    const applyMoveInDateFilter = () => {
        // 입력값을 숫자로 변환
        const minYearsValue = minYears ? parseInt(minYears, 10) : null;
        const maxYearsValue = maxYears ? parseInt(maxYears, 10) : null;
        
        setIsMoveInDateFilterActive(minYearsValue !== null || maxYearsValue !== null);
        
        // 상위 컴포넌트로 입주일 필터 정보 전달
        onMoveInDateFilterChange({
            minYears: minYearsValue,
            maxYears: maxYearsValue
        });
        
        setShowMoveInDateTooltip(false);
    };
    
    // 입주일 필터 초기화 함수 수정
    const resetMoveInDateFilter = () => {
        setMinYears('');
        setMaxYears('');
        setIsMoveInDateFilterActive(false);
        
        onMoveInDateFilterChange({
            minYears: null,
            maxYears: null
        });
        
        setShowMoveInDateTooltip(false);
    };

    // 연수 입력 처리 함수 (숫자만 허용)
    const handleYearsInput = (e, setter) => {
        const value = e.target.value;
        // 숫자만 허용
        if (/^[0-9]*$/.test(value)) {
            setter(value);
        }
    };

    // 만원 단위를 억원 단위로 변환하는 함수
    const formatPriceDisplay = (value) => {
        if (!value) return '';
        const numericValue = typeof value === 'string' ? value.replace(/,/g, '') : value.toString();
        const numberValue = parseInt(numericValue, 10);
        if (isNaN(numberValue)) return '';
        
        if (numberValue >= 10000) {
            const billions = Math.floor(numberValue / 10000);
            const remainder = numberValue % 10000;
            if (remainder === 0) {
                return `${billions}억원`;
            } else {
                const millions = Math.round(remainder / 1000) * 1000;
                return `${billions}억 ${millions.toLocaleString()}만원`;
            }
        } else {
            return `${numberValue.toLocaleString()}만원`;
        }
    };

    // 가격 입력 시 숫자와 콤마만 입력되도록 처리하는 함수
    const handlePriceInput = (e, setter) => {
        const value = e.target.value;
        // 숫자와 콤마만 허용
        if (/^[0-9,]*$/.test(value)) {
            // 콤마 제거 후 숫자만 추출
            const numericValue = value.replace(/,/g, '');
            // 콤마 추가하여 표시
            if (numericValue) {
                setter(numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ','));
            } else {
                setter('');
            }
        }
    };
    
    // 세대수 입력 시 숫자만 입력되도록 처리하는 함수
    const handleHouseholdInput = (e, setter) => {
        const value = e.target.value;
        // 숫자만 허용
        if (/^[0-9]*$/.test(value)) {
            setter(value);
        }
    };
    
    // 전용면적 입력 시 숫자와 소수점만 입력되도록 처리하는 함수
    const handleAreaInput = (e, setter) => {
        const value = e.target.value;
        // 숫자와 소수점만 허용 (소수점은 하나만)
        if (/^[0-9]*\.?[0-9]*$/.test(value)) {
            setter(value);
        }
    };

    // 가격 필터 툴팁 토글
    const togglePriceTooltip = () => {
        // 지역이 선택되지 않았을 경우 경고 메시지 표시
        if (!selectedRegion) {
            alert("지역을 먼저 선택하세요");
            return;
        }
        
        setShowPriceTooltip(!showPriceTooltip);
        setShowHouseholdTooltip(false); // 다른 툴팁 닫기
        setShowAreaTooltip(false); // 다른 툴팁 닫기
        setShowGapTooltip(false); // 전매갭 툴팁 닫기
        setShowMoveInDateTooltip(false); // 입주일 툴팁 닫기
    };
    
    // 총세대수 필터 툴팁 토글
    const toggleHouseholdTooltip = () => {
        // 지역이 선택되지 않았을 경우 경고 메시지 표시
        if (!selectedRegion) {
            alert("지역을 먼저 선택하세요");
            return;
        }
        
        setShowHouseholdTooltip(!showHouseholdTooltip);
        setShowPriceTooltip(false); // 다른 툴팁 닫기
        setShowAreaTooltip(false); // 다른 툴팁 닫기
        setShowMoveInDateTooltip(false); // 입주일 툴팁 닫기
        setShowGapTooltip(false); // 전매갭 툴팁 닫기
    };
    
    // 전용면적 필터 툴팁 토글
    const toggleAreaTooltip = () => {
        // 지역이 선택되지 않았을 경우 경고 메시지 표시
        if (!selectedRegion) {
            alert("지역을 먼저 선택하세요");
            return;
        }
        
        setShowAreaTooltip(!showAreaTooltip);
        setShowPriceTooltip(false); // 다른 툴팁 닫기
        setShowHouseholdTooltip(false); // 다른 툴팁 닫기
        setShowMoveInDateTooltip(false); // 입주일 툴팁 닫기
        setShowGapTooltip(false); // 전매갭 툴팁 닫기
    };

    // 전매갭 필터 툴팁 토글 함수
    const toggleGapTooltip = () => {
        if (!selectedRegion) {
            alert("지역을 먼저 선택하세요");
            return;
        }
        
        setShowGapTooltip(!showGapTooltip);
        // 다른 툴팁들 닫기
        setShowPriceTooltip(false);
        setShowHouseholdTooltip(false);
        setShowAreaTooltip(false);
        setShowMoveInDateTooltip(false); // 입주일 툴팁 닫기
    };

    // 입주일 필터 툴팁 토글 함수
    const toggleMoveInDateTooltip = () => {
        if (!selectedRegion) {
            alert("지역을 먼저 선택하세요");
            return;
        }
        
        setShowMoveInDateTooltip(!showMoveInDateTooltip);
        // 다른 툴팁들 닫기
        setShowPriceTooltip(false);
        setShowHouseholdTooltip(false);
        setShowAreaTooltip(false);
        setShowGapTooltip(false);
    };

    // 동 필터 툴팁 토글 함수
    const toggleSubdistrictTooltip = () => {
        if (!selectedRegion) {
            alert("지역을 먼저 선택하세요");
            return;
        }
        
        setShowSubdistrictTooltip(!showSubdistrictTooltip);
        // 다른 툴팁들 닫기
        setShowPriceTooltip(false);
        setShowHouseholdTooltip(false);
        setShowAreaTooltip(false);
        setShowGapTooltip(false);
        setShowMoveInDateTooltip(false);
    };

    // 동 체크박스 변경 핸들러
    const handleSubdistrictChange = (subdistrict) => {
        let newSelectedSubdistricts;
        
        if (selectedSubdistricts.includes(subdistrict)) {
            // 이미 선택된 동이면 제거
            newSelectedSubdistricts = selectedSubdistricts.filter(item => item !== subdistrict);
        } else {
            // 선택되지 않은 동이면 추가
            newSelectedSubdistricts = [...selectedSubdistricts, subdistrict];
        }
        
        setSelectedSubdistricts(newSelectedSubdistricts);
    };

    // 모든 동 선택/해제 핸들러
    const handleSelectAllSubdistricts = (selectAll) => {
        if (selectAll) {
            setSelectedSubdistricts([...subdistrictOptions]);
        } else {
            setSelectedSubdistricts([]);
        }
    };

    // 동 필터 적용 함수 수정
    const applySubdistrictFilter = () => {
        // 선택된 동이 없는 경우 처리
        if (selectedSubdistricts.length === 0) {
            // 선택된 동이 없으면 필터 활성화 (아무것도 표시하지 않음)
            setIsSubdistrictFilterActive(true);
        } else {
            // 모든 동이 선택된 경우 필터를 비활성화
            const isAllSelected = selectedSubdistricts.length === subdistrictOptions.length;
            setIsSubdistrictFilterActive(!isAllSelected && selectedSubdistricts.length > 0);
        }
        
        // Context의 실제 필터 상태 업데이트
        setSubdistrictFilter(selectedSubdistricts);
        
        // 상위 컴포넌트로 동 필터 정보 전달
        onSubdistrictFilterChange(selectedSubdistricts);
        
        setShowSubdistrictTooltip(false);
    };

    // 동 필터 초기화 함수 수정
    const resetSubdistrictFilter = () => {
        // 모든 동을 선택 상태로 설정 - 현재 옵션으로 업데이트
        const currentOptions = [...subdistrictOptions];
        setSelectedSubdistricts(currentOptions);
        setIsSubdistrictFilterActive(false);
        
        // 상위 컴포넌트로 필터 초기화 전달 - 빈 배열이 아닌 null을 전달하여 모든 동 표시
        onSubdistrictFilterChange(null);
        
        setShowSubdistrictTooltip(false);
    };

    // 지하철 필터 툴팁 토글 함수
    const toggleSubwayTooltip = () => {
        if (!selectedRegion) {
            alert("지역을 먼저 선택하세요");
            return;
        }
        
        setShowSubwayTooltip(!showSubwayTooltip);
        // 다른 툴팁들 닫기
        setShowPriceTooltip(false);
        setShowHouseholdTooltip(false);
        setShowAreaTooltip(false);
        setShowGapTooltip(false);
        setShowMoveInDateTooltip(false);
        setShowSubdistrictTooltip(false);
        setShowWalkingTimeTooltip(false);
    };

    // 도보 시간 필터 툴팁 토글 함수
    const toggleWalkingTimeTooltip = () => {
        if (!selectedRegion) {
            alert("지역을 먼저 선택하세요");
            return;
        }
        
        setShowWalkingTimeTooltip(!showWalkingTimeTooltip);
        // 다른 툴팁들 닫기
        setShowPriceTooltip(false);
        setShowHouseholdTooltip(false);
        setShowAreaTooltip(false);
        setShowGapTooltip(false);
        setShowMoveInDateTooltip(false);
        setShowSubdistrictTooltip(false);
        setShowSubwayTooltip(false);
        setShowGangnamTimeTooltip(false);
    };

    // 강남역 소요시간 필터 툴팁 토글 함수
    const toggleGangnamTimeTooltip = () => {
        if (!selectedRegion) {
            alert("지역을 먼저 선택하세요");
            return;
        }
        
        setShowGangnamTimeTooltip(!showGangnamTimeTooltip);
        // 다른 툴팁들 닫기
        setShowPriceTooltip(false);
        setShowHouseholdTooltip(false);
        setShowAreaTooltip(false);
        setShowGapTooltip(false);
        setShowMoveInDateTooltip(false);
        setShowSubdistrictTooltip(false);
        setShowSubwayTooltip(false);
        setShowWalkingTimeTooltip(false);
        setShowSchoolTooltip(false);
    };

    // 초등학교 필터 툴팁 토글 함수
    const toggleSchoolTooltip = () => {
        if (!selectedRegion) {
            alert("지역을 먼저 선택하세요");
            return;
        }
        
        setShowSchoolTooltip(!showSchoolTooltip);
        // 다른 툴팁들 닫기
        setShowPriceTooltip(false);
        setShowHouseholdTooltip(false);
        setShowAreaTooltip(false);
        setShowGapTooltip(false);
        setShowMoveInDateTooltip(false);
        setShowSubdistrictTooltip(false);
        setShowSubwayTooltip(false);
        setShowWalkingTimeTooltip(false);
        setShowGangnamTimeTooltip(false);
    };

    // 지하철 필터 적용 함수 수정
    const applySubwayFilter = () => {
        // 선택된 노선이 있는 경우 필터 활성화
        const isActive = selectedSubwayLines.length > 0;
        
        setIsSubwayFilterActive(isActive);
        
        // Context의 실제 필터 상태 업데이트
        const filterValue = isActive ? {
            lines: selectedSubwayLines,
            maxWalkingTime: maxWalkingTime
        } : {
            lines: null,
            maxWalkingTime: maxWalkingTime
        };
        setSubwayFilter(filterValue);
        
        // 상위 컴포넌트로 지하철 필터 정보 전달
        if (isActive) {
            onSubwayFilterChange({
                lines: selectedSubwayLines,
                maxWalkingTime: maxWalkingTime
            });
        } else {
            // 선택된 노선이 없으면 도보 시간만 적용
            onSubwayFilterChange({
                lines: null,
                maxWalkingTime: maxWalkingTime
            });
        }
        
        setShowSubwayTooltip(false);
    };

    // 도보 시간 필터 적용 함수
    const applyWalkingTimeFilter = () => {
        // 도보 시간이 설정된 경우 필터 활성화
        const isActive = maxWalkingTime !== null;
        
        setIsWalkingTimeFilterActive(isActive);
        
        // 상위 컴포넌트로 지하철 필터 정보 전달
        if (isActive) {
            onSubwayFilterChange({
                lines: selectedSubwayLines.length > 0 ? selectedSubwayLines : null,
                maxWalkingTime: maxWalkingTime
            });
        } else {
            // 도보 시간이 없으면 노선만 적용
            onSubwayFilterChange({
                lines: selectedSubwayLines.length > 0 ? selectedSubwayLines : null,
                maxWalkingTime: null
            });
        }
        
        setShowWalkingTimeTooltip(false);
    };

    // 지하철 필터 초기화 함수 수정
    const resetSubwayFilter = () => {
        setSelectedSubwayLines([]);
        setIsSubwayFilterActive(false);
        
        // 상위 컴포넌트로 필터 초기화 전달 (지하철 노선 관련 정보만 초기화)
        if (maxWalkingTime !== null) {
            // 도보 시간은 유지하되 노선 정보만 초기화
            onSubwayFilterChange({
                lines: null,
                maxWalkingTime: maxWalkingTime
            });
        } else {
            // 도보 시간도 설정되어 있지 않으면 필터 자체를 null로 설정
            onSubwayFilterChange(null);
        }
        
        setShowSubwayTooltip(false);
    };

    // 도보 시간 필터 초기화 함수
    const resetWalkingTimeFilter = () => {
        setMaxWalkingTime(null);
        setIsWalkingTimeFilterActive(false);
        
        // 상위 컴포넌트로 필터 초기화 전달
        if (selectedSubwayLines.length > 0) {
            // 노선 정보는 유지하되 도보 시간 정보만 초기화
            onSubwayFilterChange({
                lines: selectedSubwayLines,
                maxWalkingTime: null
            });
        } else {
            // 노선 정보도 없으면 필터 자체를 null로 설정
            onSubwayFilterChange(null);
        }
        
        setShowWalkingTimeTooltip(false);
    };

    // 도보 시간 선택 핸들러
    const handleWalkingTimeChange = (minutes) => {
        // 같은 값을 다시 클릭하면 선택 해제 (토글)
        if (maxWalkingTime === minutes) {
            setMaxWalkingTime(null);
        } else {
            setMaxWalkingTime(minutes);
        }
    };

    // 강남역 소요시간 선택 핸들러
    const handleGangnamTimeChange = (minutes) => {
        // 같은 값을 다시 클릭하면 선택 해제 (토글)
        if (maxGangnamTime === minutes) {
            setMaxGangnamTime(null);
        } else {
            setMaxGangnamTime(minutes);
        }
    };

    // 강남역 소요시간 필터 적용 함수
    const applyGangnamTimeFilter = () => {
        // 강남역 소요시간이 설정된 경우 필터 활성화
        const isActive = maxGangnamTime !== null;
        
        setIsGangnamTimeFilterActive(isActive);
        
        // Context의 실제 필터 상태 업데이트
        setGangnamTimeFilter({
            maxTime: maxGangnamTime
        });
        
        // 상위 컴포넌트로 강남역 소요시간 필터 정보 전달
        if (onGangnamTimeFilterChange) {
            onGangnamTimeFilterChange({
                maxTime: maxGangnamTime
            });
        }
        
        setShowGangnamTimeTooltip(false);
    };

    // 강남역 소요시간 필터 초기화 함수
    const resetGangnamTimeFilter = () => {
        setMaxGangnamTime(null);
        setIsGangnamTimeFilterActive(false);
        
        // 상위 컴포넌트로 필터 초기화 전달
        if (onGangnamTimeFilterChange) {
            onGangnamTimeFilterChange(null);
        }
        
        setShowGangnamTimeTooltip(false);
    };

    // 초등학교 필터 적용 함수
    const applySchoolFilter = () => {
        // 초등학교 필터 활성화
        setIsSchoolFilterActive(true);
        
        // Context의 실제 필터 상태 업데이트
        setSchoolFilter(hasSchool);
        
        // 상위 컴포넌트로 초등학교 필터 정보 전달
        if (onSchoolFilterChange) {
            onSchoolFilterChange({
                hasSchool: hasSchool
            });
        }
        
        setShowSchoolTooltip(false);
    };

    // 초등학교 필터 초기화 함수
    const resetSchoolFilter = () => {
        setHasSchool(true); // 초기값은 "초등학교 있음"
        setIsSchoolFilterActive(false);
        
        // 상위 컴포넌트로 필터 초기화 전달
        if (onSchoolFilterChange) {
            onSchoolFilterChange(null);
        }
        
        setShowSchoolTooltip(false);
    };

    // 지하철 노선 체크박스 변경 핸들러
    const handleSubwayLineChange = (lineName) => {
        let newSelectedLines;
        
        if (selectedSubwayLines.includes(lineName)) {
            // 이미 선택된 노선이면 제거
            newSelectedLines = selectedSubwayLines.filter(item => item !== lineName);
        } else {
            // 선택되지 않은 노선이면 추가
            newSelectedLines = [...selectedSubwayLines, lineName];
        }
        
        setSelectedSubwayLines(newSelectedLines);
    };

    // 모든 지하철 노선 선택/해제 핸들러
    const handleSelectAllSubwayLines = (selectAll) => {
        if (selectAll) {
            setSelectedSubwayLines(subwayLines.map(item => item.name));
        } else {
            setSelectedSubwayLines([]);
        }
    };

    // 필터 초기화 함수 수정
    const resetAllFilters = () => {
        // 모든 필터 상태 초기화
        setMinPrice('');
        setMaxPrice('');
        setIsPriceFilterActive(false);
        
        setMinHousehold('');
        setMaxHousehold('');
        setIsHouseholdFilterActive(false);
        
        setMinArea('');
        setMaxArea('');
        setIsAreaFilterActive(false);
        
        setMinGap('');
        setMaxGap('');
        setIsGapFilterActive(false);
        
        setMinYears('');
        setMaxYears('');
        setIsMoveInDateFilterActive(false);
        
        // Context의 실제 필터 상태 초기화
        setPriceFilter({ minPrice: null, maxPrice: null });
        setHouseholdFilter({ minHousehold: null, maxHousehold: null });
        setAreaFilter({ minArea: null, maxArea: null });
        setGapFilter({ minGap: null, maxGap: null });
        setMoveInDateFilter({ minYears: null, maxYears: null });
        
        // 동 필터 초기화 - 현재 옵션으로 업데이트
        const currentOptions = [...subdistrictOptions];
        setSelectedSubdistricts(currentOptions);
        setIsSubdistrictFilterActive(false);
        
        // 지하철 필터 초기화
        setSelectedSubwayLines([]);
        setMaxWalkingTime(null);
        setIsSubwayFilterActive(false);
        setIsWalkingTimeFilterActive(false);
        
        // 강남역 소요시간 필터 초기화
        setMaxGangnamTime(null);
        setIsGangnamTimeFilterActive(false);
        
        // 초등학교 필터 초기화
        setHasSchool(true);
        setIsSchoolFilterActive(false);
        
        // Context의 나머지 필터 상태 초기화
        setSubdistrictFilter(null);
        setSubwayFilter(null);
        setGangnamTimeFilter({ maxTime: null });
        setSchoolFilter(null);
        
        // 상위 컴포넌트로 모든 필터 초기화 전달
        onPriceFilterChange({ minPrice: null, maxPrice: null });
        onHouseholdFilterChange({ minHousehold: null, maxHousehold: null });
        onAreaFilterChange({ minArea: null, maxArea: null });
        onGapFilterChange({ minGap: null, maxGap: null });
        onMoveInDateFilterChange({ minYears: null, maxYears: null });
        onSubdistrictFilterChange(null);
        onSubwayFilterChange(null);
        if (onGangnamTimeFilterChange) {
            onGangnamTimeFilterChange(null);
        }
        if (onSchoolFilterChange) {
            onSchoolFilterChange(null);
        }
        
        // 모든 툴팁 닫기
        setShowPriceTooltip(false);
        setShowHouseholdTooltip(false);
        setShowAreaTooltip(false);
        setShowGapTooltip(false);
        setShowMoveInDateTooltip(false);
        setShowSubdistrictTooltip(false);
        setShowSubwayTooltip(false);
        setShowWalkingTimeTooltip(false);
        setShowGangnamTimeTooltip(false);
        setShowSchoolTooltip(false);
    };

    // 필터가 하나라도 적용되어 있는지 확인하는 변수
    const isAnyFilterActive = isPriceFilterActive || isHouseholdFilterActive || isAreaFilterActive || 
                             isGapFilterActive || isMoveInDateFilterActive || isSubdistrictFilterActive || 
                             isSubwayFilterActive || isWalkingTimeFilterActive || isGangnamTimeFilterActive || 
                             isSchoolFilterActive;

    return (
        <div className="flex justify-center w-full">
            <section className="flex flex-col box-border m-1 p-1.5 w-full sm:w-[56vw]">
                {/* 필터 컨테이너 - 항상 표시되도록 수정 */}
                <div className="flex flex-col md:flex-row justify-around p-2 mb-4 bg-white rounded-lg md:rounded-full shadow-lg">
                    <RegionDropdownMetrix 
                        selectedRegion={selectedRegion}
                        selectedCity={selectedCities.length === 1 ? selectedCities[0].cortarNo : ""}
                        onRegionChange={handleRegionChange}
                        onCityChange={handleCityChange}
                        siDoData={siDoData}
                        siGunGuData={siGunGuData}
                    />

                    {/* 필터 버튼들을 그리드로 배치 */}
                    <div className="grid grid-cols-2 md:flex md:flex-row gap-2 mt-2 md:mt-0">
                        {/* 입주일 필터 버튼 */}
                        <div className="relative">
                            <button 
                                ref={moveInDateButtonRef}
                                onClick={toggleMoveInDateTooltip}
                                className={`px-2 py-1 text-xs md:text-sm rounded-full w-full ${
                                    isMoveInDateFilterActive 
                                    ? " bg-green-600 text-white font-medium" 
                                    : " text-gray-500 bg-gray-50 border-gray-200 hover:bg-gray-200 hover:text-gray-600"
                                } ${(!data || data.length === 0) ? " opacity-100" : ""}`}
                            >
                                {isMoveInDateFilterActive ? '입주일적용' : '입주일'}
                            </button>
                            
                            {/* 입주일 필터 툴팁 */}
                            {showMoveInDateTooltip && (
                                <div 
                                    ref={moveInDateTooltipRef}
                                    className="absolute md:right-5 md:right-1/2 md:transform md:translate-x-1/2 top-full mt-2 w-64 p-3 bg-white rounded-lg shadow-xl z-50 border border-gray-200"
                                >
                                    <div className="absolute -top-2 right-1/2 transform translate-x-1/2 w-4 h-4 bg-white border-t border-l border-gray-200 transform rotate-45"></div>
                                
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="text-sm font-bold">연식설정</h3>
                                        <button onClick={() => setShowMoveInDateTooltip(false)}>
                                            <MdOutlineCancel size={18} />
                                        </button>
                                    </div>
                                    <div className="flex items-center justify-between mb-3">
                                        <input
                                            type="text"
                                            value={minYears}
                                            onChange={(e) => handleYearsInput(e, setMinYears)}
                                            placeholder="최소 연수"
                                            className="w-24 p-1 border border-gray-300 rounded-lg text-center text-sm"
                                        />
                                        <span className="mx-1">~</span>
                                        <input
                                            type="text"
                                            value={maxYears}
                                            onChange={(e) => handleYearsInput(e, setMaxYears)}
                                            placeholder="최대 연수"
                                            className="w-24 p-1 border border-gray-300 rounded-lg text-center text-sm"
                                        />
                                    </div>
                                    <div className="text-xs text-gray-500 mb-2 text-right">
                                        (단위: 년)
                                    </div>
                                    <div className="flex justify-end space-x-2">
                                        <button
                                            onClick={resetMoveInDateFilter}
                                            className="px-2 py-1 text-xs text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                                        >
                                            초기화
                                        </button>
                                        <button
                                            onClick={applyMoveInDateFilter}
                                            className="px-2 py-1 text-xs text-white bg-green-600 rounded-lg hover:bg-green-700"
                                        >
                                            적용
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* 전용면적 필터 버튼 */}
                        <div className="relative">
                            <button 
                                ref={areaButtonRef}
                                onClick={toggleAreaTooltip}
                                className={`px-2 py-1 text-xs md:text-sm rounded-full w-full ${
                                    isAreaFilterActive 
                                    ? "bg-green-600 text-white font-medium" 
                                    : "text-gray-500 bg-gray-50 border-gray-200 hover:bg-gray-200 hover:text-gray-600"
                                } ${(!data || data.length === 0) ? " opacity-100" : ""}`}
                            >
                                {isAreaFilterActive ? '면적적용' : '전용면적'}
                            </button>
                            
                            {/* 전용면적 필터 툴팁 */}
                            {showAreaTooltip && (
                            <div 
                                ref={areaTooltipRef}
                                className="absolute right-0 md:right-1/2 md:transform md:translate-x-1/2 top-full mt-2 w-64 p-3 bg-white rounded-lg shadow-xl z-50 border border-gray-200"
                        >
                            {/* Tooltip tail/arrow */}
                            <div className="absolute -top-2 right-1/2 transform translate-x-1/2 w-4 h-4 bg-white border-t border-l border-gray-200 transform rotate-45"></div>
                            
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className="text-sm font-bold">전용면적 설정</h3>
                                    <button onClick={() => setShowAreaTooltip(false)}>
                                        <MdOutlineCancel size={18} />
                                    </button>
                                </div>
                                <div className="flex items-center justify-between mb-3">
                                    <input
                                        type="text"
                                        value={minArea}
                                        onChange={(e) => handleAreaInput(e, setMinArea)}
                                        placeholder="최소 면적"
                                        className="w-24 p-1 border border-gray-300 rounded-lg text-center text-sm"
                                    />
                                    <span className="mx-1">~</span>
                                    <input
                                        type="text"
                                        value={maxArea}
                                        onChange={(e) => handleAreaInput(e, setMaxArea)}
                                        placeholder="최대 면적"
                                        className="w-24 p-1 border border-gray-300 rounded-lg text-center text-sm"
                                    />
                                </div>
                                <div className="text-xs text-gray-500 mb-2 text-right">
                                    (단위: m²)
                                </div>
                                <div className="flex justify-end space-x-2">
                                    <button
                                        onClick={resetAreaFilter}
                                        className="px-2 py-1 text-xs text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                                    >
                                        초기화
                                    </button>
                                    <button
                                        onClick={applyAreaFilter}
                                        className="px-2 py-1 text-xs text-white bg-green-600 rounded-lg hover:bg-green-700"
                                    >
                                            적용
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* 총세대수 필터 버튼 */}
                    <div className="relative">
                        <button 
                            ref={householdButtonRef}
                            onClick={toggleHouseholdTooltip}
                            className={`px-2 py-1 text-xs md:text-sm rounded-full w-full ${
                                isHouseholdFilterActive 
                                ? "bg-green-600 text-white font-medium" 
                                : "text-gray-500 bg-gray-50 border-gray-200 hover:bg-gray-200 hover:text-gray-600"
                            } ${(!data || data.length === 0) ? " opacity-100" : ""}`}
                        >
                            {isHouseholdFilterActive ? '세대수적용' : '세대수'}
                        </button>
                        
                        {/* 총세대수 필터 툴팁 */}
                        {showHouseholdTooltip && (
                            <div 
                                ref={householdTooltipRef}
                                className="absolute md:right-5 md:transform md:translate-x-1/2 top-full mt-2 w-64 p-3 bg-white rounded-lg shadow-xl z-50 border border-gray-200"
                            >
                                {/* Tooltip tail/arrow */}
                                <div className="absolute -top-2 right-1/2 transform translate-x-1/2 w-4 h-4 bg-white border-t border-l border-gray-200 transform rotate-45"></div>
                                
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className="text-sm font-bold">총세대수 설정</h3>
                                    <button onClick={() => setShowHouseholdTooltip(false)}>
                                        <MdOutlineCancel size={18} />
                                    </button>
                                </div>
                                <div className="flex items-center justify-between mb-3">
                                    <input
                                        type="text"
                                        value={minHousehold}
                                        onChange={(e) => handleHouseholdInput(e, setMinHousehold)}
                                        placeholder="최소 세대수"
                                        className="w-24 p-1 border border-gray-300 rounded-lg text-center text-sm"
                                    />
                                    <span className="mx-1">~</span>
                                    <input
                                        type="text"
                                        value={maxHousehold}
                                        onChange={(e) => handleHouseholdInput(e, setMaxHousehold)}
                                        placeholder="최대 세대수"
                                        className="w-24 p-1 border border-gray-300 rounded-lg text-center text-sm"
                                    />
                                </div>
                                <div className="text-xs text-gray-500 mb-2 text-right">
                                    (단위: 세대)
                                </div>
                                <div className="flex justify-end space-x-2">
                                    <button
                                        onClick={resetHouseholdFilter}
                                        className="px-2 py-1 text-xs text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                                    >
                                        초기화
                                    </button>
                                    <button
                                        onClick={applyHouseholdFilter}
                                        className="px-2 py-1 text-xs text-white bg-green-600 rounded-lg hover:bg-green-700"
                                    >
                                        적용
                                    </button>
                                </div>
                                    </div>
                        )}
                                    </div>
                    
                        {/* 가격 필터 버튼 */}
                        <div className="relative">
                            <button 
                                ref={priceButtonRef}
                                onClick={togglePriceTooltip}
                                className={`px-2 py-1 text-xs md:text-sm rounded-full w-full ${
                                    isPriceFilterActive 
                                    ? "bg-green-600 text-white font-medium" 
                                    : "text-gray-500 bg-gray-50 border-gray-200 hover:bg-gray-200 hover:text-gray-600"
                                } ${(!data || data.length === 0) ? " opacity-100" : ""}`}
                            >
                                {isPriceFilterActive ? '매매가적용' : '매매가'}
                            </button>
                            
                            {/* 가격 필터 툴팁 */}
                            {showPriceTooltip && (
                                <div 
                                    ref={priceTooltipRef}
                                    className="absolute right-0 md:right-1/2 md:transform md:translate-x-1/2 top-full mt-2 w-64 p-3 bg-white rounded-lg shadow-xl z-50 border border-gray-200"
                                >
                                    {/* Tooltip tail/arrow */}
                                    <div className="absolute -top-2 right-1/2 transform translate-x-1/2 w-4 h-4 bg-white border-t border-l border-gray-200 transform rotate-45"></div>
                                    
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="text-sm font-bold">매매가 설정</h3>
                                        <button onClick={() => setShowPriceTooltip(false)}>
                                            <MdOutlineCancel size={18} />
                                        </button>
                                    </div>
                                    <div className="flex items-center justify-between mb-3">
                                        <input
                                            type="text"
                                            value={minPrice}
                                            onChange={(e) => handlePriceInput(e, setMinPrice)}
                                            placeholder="최소 금액"
                                            className="w-24 p-1 border border-gray-300 rounded-lg text-center text-sm"
                                        />
                                        <span className="mx-1">~</span>
                                        <input
                                            type="text"
                                            value={maxPrice}
                                            onChange={(e) => handlePriceInput(e, setMaxPrice)}
                                            placeholder="최대 금액"
                                            className="w-24 p-1 border border-gray-300 rounded-lg text-center text-sm"
                                        />
                                        </div>
                                    <div className="text-xs text-gray-500 mb-2">
                                        <div className="text-right">(단위: 만원)</div>
                                        {(minPrice || maxPrice) && (
                                            <div className="mt-1 p-2 bg-gray-50 rounded text-gray-700">
                                                {minPrice && <div>최소: {formatPriceDisplay(minPrice)}</div>}
                                                {maxPrice && <div>최대: {formatPriceDisplay(maxPrice)}</div>}
                                            </div>
                                        )}
                                        </div>
                                    <div className="flex justify-end space-x-2">
                                        <button
                                            onClick={resetPriceFilter}
                                            className="px-2 py-1 text-xs text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                                        >
                                            초기화
                                        </button>
                                        <button
                                            onClick={applyPriceFilter}
                                            className="px-2 py-1 text-xs text-white bg-green-600 rounded-lg hover:bg-green-700"
                                        >
                                            적용
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        {/* 전매갭 필터 버튼 */}
                        <div className="relative">
                            <button 
                                ref={gapButtonRef}
                                onClick={toggleGapTooltip}
                                className={`px-2 py-1 text-xs md:text-sm rounded-full w-full ${
                                    isGapFilterActive 
                                    ? "bg-green-600 text-white font-medium" 
                                    : "text-gray-500 bg-gray-50 border-gray-200 hover:bg-gray-200 hover:text-gray-600"
                                } ${(!data || data.length === 0) ? " opacity-100" : ""}`}
                            >
                                {isGapFilterActive ? '전매갭적용' : '전매갭'}
                            </button>
                            
                            {/* 전매갭 필터 툴팁 */}
                            {showGapTooltip && (
                                <div 
                                    ref={gapTooltipRef}
                                    className="absolute md:right-5 md:transform md:translate-x-1/2 top-full mt-2 w-64 p-3 bg-white rounded-lg shadow-xl z-50 border border-gray-200"
                                >
                                    <div className="absolute -top-2 right-1/2 transform translate-x-1/2 w-4 h-4 bg-white border-t border-l border-gray-200 transform rotate-45"></div>
                                    
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="text-sm font-bold">전매갭 설정</h3>
                                        <button onClick={() => setShowGapTooltip(false)}>
                                            <MdOutlineCancel size={18} />
                                        </button>
                                    </div>
                                    <div className="flex items-center justify-between mb-3">
                                        <input
                                            type="text"
                                            value={minGap}
                                            onChange={(e) => handleGapInput(e, setMinGap)}
                                            placeholder="최소 전매갭"
                                            className="w-24 p-1 border border-gray-300 rounded-lg text-center text-sm"
                                        />
                                        <span className="mx-1">~</span>
                                        <input
                                            type="text"
                                            value={maxGap}
                                            onChange={(e) => handleGapInput(e, setMaxGap)}
                                            placeholder="최대 전매갭"
                                            className="w-24 p-1 border border-gray-300 rounded-lg text-center text-sm"
                                        />
                                    </div>
                                    <div className="text-xs text-gray-500 mb-2 text-right">
                                        (단위: 만원)
                                    </div>
                                    <div className="flex justify-end space-x-2">
                                        <button
                                            onClick={resetGapFilter}
                                            className="px-2 py-1 text-xs text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                                        >
                                            초기화
                                        </button>
                                        <button
                                            onClick={applyGapFilter}
                                            className="px-2 py-1 text-xs text-white bg-green-600 rounded-lg hover:bg-green-700"
                                        >
                                            적용
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* 동 필터 버튼 */}
                        <div className="relative">
                            <button 
                                ref={subdistrictButtonRef}
                                onClick={toggleSubdistrictTooltip}
                                className={`px-2 py-1 text-xs md:text-sm rounded-full w-full ${
                                    isSubdistrictFilterActive 
                                    ? "bg-green-600 text-white font-medium" 
                                    : "text-gray-500 bg-gray-50 border-gray-200 hover:bg-gray-200 hover:text-gray-600"
                                } ${(!data || data.length === 0) ? " opacity-100" : ""}`}
                            >
                                {isSubdistrictFilterActive ? '동필터적용' : '동필터'}
                            </button>
                            
                            {/* 동 필터 툴팁 */}
                            {showSubdistrictTooltip && (
                                <div 
                                    ref={subdistrictTooltipRef}
                                    className="absolute right-0 md:right-1/2 md:transform md:translate-x-1/2 top-full mt-2 w-64 p-3 bg-white rounded-lg shadow-xl z-50 border border-gray-200"
                                >
                                    <div className="absolute -top-2 right-1/2 transform translate-x-1/2 w-4 h-4 bg-white border-t border-l border-gray-200 transform rotate-45"></div>
                                    
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="text-sm font-bold">동 필터 설정</h3>
                                        <button onClick={() => setShowSubdistrictTooltip(false)}>
                                            <MdOutlineCancel size={18} />
                                        </button>
                                    </div>
                                    
                                    <div className="flex items-center justify-between mb-2 text-sm">
                                        <button 
                                            onClick={() => handleSelectAllSubdistricts(true)}
                                            className="px-2 py-1 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 rounded"
                                        >
                                            전체 선택
                                        </button>
                                        <button 
                                            onClick={() => handleSelectAllSubdistricts(false)}
                                            className="px-2 py-1 text-xs bg-gray-50 hover:bg-gray-100 text-gray-700 rounded"
                                        >
                                            전체 해제
                                        </button>
                                    </div>
                                    
                                    <div className="max-h-40 overflow-y-auto mb-3 border border-gray-100 rounded p-1">
                                        {subdistrictOptions.map((subdistrict, index) => (
                                            <div key={index} className="flex items-center mb-1">
                                                <input
                                                    type="checkbox"
                                                    id={`subdistrict-${index}`}
                                                    checked={selectedSubdistricts.includes(subdistrict)}
                                                    onChange={() => handleSubdistrictChange(subdistrict)}
                                                    className="mr-2"
                                                />
                                                <label htmlFor={`subdistrict-${index}`} className="text-sm">
                                                    {subdistrict}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                    
                                    <div className="flex justify-end space-x-2">
                                        <button
                                            onClick={resetSubdistrictFilter}
                                            className="px-2 py-1 text-xs text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                                        >
                                            초기화
                                        </button>
                                        <button
                                            onClick={applySubdistrictFilter}
                                            className="px-2 py-1 text-xs text-white bg-green-600 rounded-lg hover:bg-green-700"
                                        >
                                            적용
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* 초등학교 필터 버튼 */}
                        <div className="relative">
                            <button 
                                ref={schoolButtonRef}
                                onClick={toggleSchoolTooltip}
                                className={`px-2 py-1 text-xs md:text-sm rounded-full w-full ${
                                    isSchoolFilterActive 
                                    ? "bg-green-600 text-white font-medium" 
                                    : "text-gray-500 bg-gray-50 border-gray-200 hover:bg-gray-200 hover:text-gray-600"
                                } ${(!data || data.length === 0) ? " opacity-100" : ""}`}
                            >
                                {isSchoolFilterActive 
                                    ? (hasSchool ? '초등학교O' : '초등학교X') 
                                    : '초등학교'}
                            </button>
                            
                            {/* 초등학교 필터 툴팁 */}
                            {showSchoolTooltip && (
                                <div 
                                    ref={schoolTooltipRef}
                                    className="absolute md:right-5 md:transform md:translate-x-1/2 top-full mt-2 w-64 p-3 bg-white rounded-lg shadow-xl z-50 border border-gray-200"
                                >
                                    <div className="absolute -top-2 right-1/2 transform translate-x-1/2 w-4 h-4 bg-white border-t border-l border-gray-200 transform rotate-45"></div>
                                    
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="text-sm font-bold">초등학교 필터</h3>
                                        <button onClick={() => setShowSchoolTooltip(false)}>
                                            <MdOutlineCancel size={18} />
                                        </button>
                                    </div>
                                    
                                    {/* 초등학교 필터 옵션 - 토글 방식 */}
                                    <div className="mb-3">
                                        <h4 className="text-sm font-medium mb-2">초등학교 유무</h4>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => setHasSchool(true)}
                                                className={`flex-1 p-1 text-xs rounded-full ${
                                                    hasSchool === true
                                                    ? "bg-blue-500 text-white"
                                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                                }`}
                                            >
                                                초등학교 있음
                                            </button>
                                            <button
                                                onClick={() => setHasSchool(false)}
                                                className={`flex-1 p-1 text-xs rounded-full ${
                                                    hasSchool === false
                                                    ? "bg-blue-500 text-white"
                                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                                }`}
                                            >
                                                초등학교 없음
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <div className="flex justify-end space-x-2">
                                        <button
                                            onClick={resetSchoolFilter}
                                            className="px-2 py-1 text-xs text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                                        >
                                            초기화
                                        </button>
                                        <button
                                            onClick={applySchoolFilter}
                                            className="px-2 py-1 text-xs text-white bg-green-600 rounded-lg hover:bg-green-700"
                                        >
                                            적용
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* 지하철 필터 버튼 */}
                        <div className="relative">
                            <button 
                                ref={subwayButtonRef}
                                onClick={toggleSubwayTooltip}
                                className={`px-2 py-1 text-xs md:text-sm rounded-full w-full ${
                                    isSubwayFilterActive 
                                    ? "bg-green-600 text-white font-medium" 
                                    : "text-gray-500 bg-gray-50 border-gray-200 hover:bg-gray-200 hover:text-gray-600"
                                } ${(!data || data.length === 0) ? " opacity-100" : ""}`}
                            >
                                {isSubwayFilterActive ? `노선(${selectedSubwayLines.length})` : '지하철노선'}
                            </button>
                            
                            {/* 지하철 필터 툴팁 - 노선만 */}
                            {showSubwayTooltip && (
                                <div 
                                    ref={subwayTooltipRef}
                                    className="absolute right-0 md:right-1/2 md:transform md:translate-x-1/2 top-full mt-2 w-64 p-3 bg-white rounded-lg shadow-xl z-50 border border-gray-200"
                                >
                                    <div className="absolute -top-2 right-1/2 transform translate-x-1/2 w-4 h-4 bg-white border-t border-l border-gray-200 transform rotate-45"></div>
                                    
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="text-sm font-bold">지하철 노선 필터 설정</h3>
                                        <button onClick={() => setShowSubwayTooltip(false)}>
                                            <MdOutlineCancel size={18} />
                                        </button>
                                    </div>
                                    
                                    <div className="flex items-center justify-between mb-2 text-sm">
                                        <button 
                                            onClick={() => handleSelectAllSubwayLines(true)}
                                            className="px-2 py-1 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 rounded"
                                        >
                                            전체 선택
                                        </button>
                                        <button 
                                            onClick={() => handleSelectAllSubwayLines(false)}
                                            className="px-2 py-1 text-xs bg-gray-50 hover:bg-gray-100 text-gray-700 rounded"
                                        >
                                            전체 해제
                                        </button>
                                    </div>
                                    
                                    <div className="max-h-60 overflow-y-auto mb-3 border border-gray-100 rounded p-1">
                                        {subwayLines.length > 0 ? (
                                            subwayLines.map((lineInfo, index) => (
                                                <div key={index} className="flex items-center mb-2">
                                                    <input
                                                        type="checkbox"
                                                        id={`subway-line-${index}`}
                                                        checked={selectedSubwayLines.includes(lineInfo.name)}
                                                        onChange={() => handleSubwayLineChange(lineInfo.name)}
                                                        className="mr-2"
                                                    />
                                                    <label htmlFor={`subway-line-${index}`} className="text-sm flex items-center">
                                                        <span 
                                                            className="inline-block w-4 h-4 rounded-full mr-2"
                                                            style={{ backgroundColor: lineInfo.color }}
                                                        ></span>
                                                        <span className="flex-grow">{lineInfo.name}</span>
                                                        <span className="text-xs text-gray-500 ml-2">
                                                            ({lineInfo.stations.length}개 역)
                                                        </span>
                                                    </label>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="text-sm text-gray-500 p-2">지하철 정보가 없습니다.</div>
                                        )}
                                    </div>
                                    
                                    <div className="flex justify-end space-x-2">
                                        <button
                                            onClick={resetSubwayFilter}
                                            className="px-2 py-1 text-xs text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                                        >
                                            초기화
                                        </button>
                                        <button
                                            onClick={applySubwayFilter}
                                            className="px-2 py-1 text-xs text-white bg-green-600 rounded-lg hover:bg-green-700"
                                        >
                                            적용
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* 도보 시간 필터 버튼 */}
                        <div className="relative">
                            <button 
                                ref={walkingTimeButtonRef}
                                onClick={toggleWalkingTimeTooltip}
                                className={`px-2 py-1 text-xs md:text-sm rounded-full w-full ${
                                    isWalkingTimeFilterActive 
                                    ? "bg-green-600 text-white font-medium" 
                                    : "text-gray-500 bg-gray-50 border-gray-200 hover:bg-gray-200 hover:text-gray-600"
                                } ${(!data || data.length === 0) ? " opacity-100" : ""}`}
                            >
                                {isWalkingTimeFilterActive ? `${maxWalkingTime}분이내` : '지하철도보시간'}
                            </button>
                            
                            {/* 도보 시간 필터 툴팁 */}
                            {showWalkingTimeTooltip && (
                                <div 
                                    ref={walkingTimeTooltipRef}
                                    className="absolute md:right-5 md:transform md:translate-x-1/2 top-full mt-2 w-64 p-3 bg-white rounded-lg shadow-xl z-50 border border-gray-200"
                                >
                                    <div className="absolute -top-2 right-1/2 transform translate-x-1/2 w-4 h-4 bg-white border-t border-l border-gray-200 transform rotate-45"></div>
                                    
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="text-sm font-bold">지하철 도보 시간 필터 설정</h3>
                                        <button onClick={() => setShowWalkingTimeTooltip(false)}>
                                            <MdOutlineCancel size={18} />
                                        </button>
                                    </div>
                                    
                                    {/* 도보 시간 필터 섹션 */}
                                    <div className="mb-3">
                                        <h4 className="text-sm font-medium mb-2">도보 시간 제한</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {[5, 10, 15, 20, 30].map(minutes => (
                                                <button
                                                    key={minutes}
                                                    onClick={() => handleWalkingTimeChange(minutes)}
                                                    className={`px-2 py-1 text-xs rounded-full ${
                                                        maxWalkingTime === minutes
                                                        ? "bg-blue-500 text-white"
                                                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                                    }`}
                                                >
                                                    {minutes}분 이내
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    <div className="flex justify-end space-x-2">
                                        <button
                                            onClick={resetWalkingTimeFilter}
                                            className="px-2 py-1 text-xs text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                                        >
                                            초기화
                                        </button>
                                        <button
                                            onClick={applyWalkingTimeFilter}
                                            className="px-2 py-1 text-xs text-white bg-green-600 rounded-lg hover:bg-green-700"
                                        >
                                            적용
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* 강남역 소요시간 필터 버튼 */}
                        <div className="relative">
                            <button 
                                ref={gangnamTimeButtonRef}
                                onClick={toggleGangnamTimeTooltip}
                                className={`px-2 py-1 text-xs md:text-sm rounded-full w-full ${
                                    isGangnamTimeFilterActive 
                                    ? "bg-green-600 text-white font-medium" 
                                    : "text-gray-500 bg-gray-50 border-gray-200 hover:bg-gray-200 hover:text-gray-600"
                                } ${(!data || data.length === 0) ? " opacity-100" : ""}`}
                            >
                                {isGangnamTimeFilterActive ? `강남${maxGangnamTime}분` : '강남역소요시간'}
                            </button>
                            
                            {/* 강남역 소요시간 필터 툴팁 */}
                            {showGangnamTimeTooltip && (
                                <div 
                                    ref={gangnamTimeTooltipRef}
                                    className="absolute right-0 md:right-1/2 md:transform md:translate-x-1/2 top-full mt-2 w-64 p-3 bg-white rounded-lg shadow-xl z-50 border border-gray-200"
                                >
                                    <div className="absolute -top-2 right-1/2 transform translate-x-1/2 w-4 h-4 bg-white border-t border-l border-gray-200 transform rotate-45"></div>
                                    
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="text-sm font-bold">강남역 소요시간 필터</h3>
                                        <button onClick={() => setShowGangnamTimeTooltip(false)}>
                                            <MdOutlineCancel size={18} />
                                        </button>
                                    </div>
                                    
                                    {/* 강남역 소요시간 필터 섹션 */}
                                    <div className="mb-3">
                                        <h4 className="text-sm font-medium mb-2">강남역까지 소요시간</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {[10, 20, 30, 40, 50, 60].map(minutes => (
                                                <button
                                                    key={minutes}
                                                    onClick={() => handleGangnamTimeChange(minutes)}
                                                    className={`px-3 py-1 text-xs rounded-full ${
                                                        maxGangnamTime === minutes
                                                        ? "bg-blue-500 text-white"
                                                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                                    }`}
                                                >
                                                    {minutes}분 이내
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    <div className="flex justify-end space-x-2">
                                        <button
                                            onClick={resetGangnamTimeFilter}
                                            className="px-2 py-1 text-xs text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                                        >
                                            초기화
                                        </button>
                                        <button
                                            onClick={applyGangnamTimeFilter}
                                            className="px-2 py-1 text-xs text-white bg-green-600 rounded-lg hover:bg-green-700"
                                        >
                                            적용
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* 모든 필터 초기화 버튼 */}
                        <div className="col-span-2 md:col-span-1">
                            <button
                                onClick={resetAllFilters}
                                disabled={!isAnyFilterActive}
                                className={`px-2 py-1 text-xs md:text-sm rounded-full w-full ${
                                    isAnyFilterActive
                                        ? "bg-orange-500 text-white font-medium hover:bg-red-600"
                                        : "text-gray-500 bg-gray-50 border-gray-200 hover:bg-gray-200 hover:text-gray-600"
                                }`}
                            >
                                필터초기화
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default FilterComponent; 